import React, { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { cn, useDeepCompareEffect } from '@companion-professional/webutils';
import { DiagnosisOption, DiagnosisOptionSets, ImageViewport, ImageSelector } from '../ImageDiagnosis';
import { ImageDxPointValues, DxImageWithPointsAndConditions } from './imageDxTypes';
import { dogPainDxImages } from './dxImages';
import { dogPainEvaluations } from './evaluations';
import { PointWithDialog } from './PointWithDialog';
import { EvaluationEntry } from '../MultipleEvaluations';
import { getFormattedPointValues, getPointNames } from './valueFormatting';

export * from './imageDxTypes';

interface AdvancedImageDiagnosisProps {
  // name is the name of the field in the form.
  name: string;

  // label that will be displayed above the images.
  label: string;

  values: ImageDxPointValues;
  onChange?: (pointValues: ImageDxPointValues) => void;

  // evaluations is the list of evaluations (e.g. "Pain upon manipulation") for the point.  Defaults to
  // dogPainEvaluations.
  evaluations?: EvaluationEntry[];

  // evaluationOptions is the list of options to use for each of the evaluations (e.g. "-", "+", "++", etc.).  Defaults
  // to dogPainEvaluations.
  evaluationOptions?: DiagnosisOption[];

  // diagnosisImages is the list of images to display.  Each image has a set of points with conditions that are related
  // to that point.  Defaults to dogPainDxImages.
  diagnosisImages?: DxImageWithPointsAndConditions[];

  // initialEvaluationValue is the default value to use for all the evaluations (e.g. pain levels). Default is 0.
  initialEvaluationValue?: number;
}

// AdvancedImageDiagnosis is a component that's used to display a set of images with points on them.  Each point, when
// clicked, opens a dialog that allows the user to select a pain level and any conditions that relate to that point.
export const AdvancedImageDiagnosis: FunctionComponent<AdvancedImageDiagnosisProps> = ({
  name,
  label,
  values,
  onChange = () => {},
  diagnosisImages = dogPainDxImages,
  evaluations = dogPainEvaluations,
  evaluationOptions = DiagnosisOptionSets.painLocationOptions,
  initialEvaluationValue = 0
}) => {
  const [currentlySelectedImage, setCurrentlySelectedImage] = useState<number>(0);
  const [currentPointValues, setCurrentPointValues] = useState<ImageDxPointValues>({});

  // pointDisplayValues contains the value that is displayed on the point when viewing the complete image.  This
  // relates to the evaluationOptions; so (given the default for DiagnosisOptionSets.painLocationOptions, if the value
  // for a point is 1, "+" will be displayed).
  const [pointDisplayValues, setPointDisplayValues] = useState<Record<string, number>>({});

  const pointNames = useMemo(() => getPointNames(diagnosisImages), [diagnosisImages]);

  // TODO: There was an issue with out of control re-renders when the evaluations prop was changed by a parent
  //   component (somewhere in DynamicForm).  This is a workaround to prevent that from happening.  We should
  //   investigate this further to figure out why this is happening and if we can fix it without the
  //   useDeepCompareEffect workaround.
  useDeepCompareEffect(() => {
    onChange(getFormattedPointValues(pointNames, evaluations, initialEvaluationValue, currentPointValues));
  }, [evaluations, currentPointValues, pointNames]);

  useDeepCompareEffect(() => {
    // Only update the currentPointValues if the passed in `values` prop value has changed (not the reference).  This
    // is to prevent unnecessary re-renders when something in the parent component updates the values (react-hook-form
    // for example).
    setCurrentPointValues(values);
  }, [values]);

  useEffect(() => {
    const newPointDisplayValues: Record<string, number> = {};
    pointNames.forEach((pointName) => {
      if (currentPointValues?.[pointName] && currentPointValues[pointName].evaluations) {
        newPointDisplayValues[pointName] = Object.values(currentPointValues[pointName].evaluations).reduce(
          // Because an evaluation value can be a string or a number, we need to check the type of each value.
          (acc, level) => Math.max(typeof acc === 'number' ? acc : 0, typeof level === 'number' ? level : 0),
          0
        ) as number;
      } else {
        newPointDisplayValues[pointName] = initialEvaluationValue;
      }
    });
    setPointDisplayValues(newPointDisplayValues);
  }, [pointNames, currentPointValues]);

  return (
    <ImageSelector
      diagnosisImages={diagnosisImages}
      fieldName={name}
      fieldLabel={label}
      currentlySelectedImage={currentlySelectedImage}
      setCurrentlySelectedImage={setCurrentlySelectedImage}
    >
      {diagnosisImages.map((diagnosisImage, pageIndex) => (
        <ImageViewport
          key={`image-${name}-${diagnosisImage.name}-${pageIndex}`}
          className={cn('bg-widget', {
            hidden: pageIndex !== currentlySelectedImage
          })}
          diagnosisImage={diagnosisImage}
        >
          {(diagnosisImage?.points || []).map((point, pointIndex) => (
            <PointWithDialog
              key={`point-${name}-${diagnosisImage.name}-${pointIndex}`}
              pageIndex={pageIndex}
              point={point}
              options={evaluationOptions}
              displayedOptionValue={pointDisplayValues[point.name] || 0}
              evaluations={evaluations}
              initialEvaluationValue={initialEvaluationValue}
              currentPointValues={currentPointValues}
              onConditionChange={(pointName, conditionName, checked) => {
                setCurrentPointValues((currentValue): ImageDxPointValues => {
                  if (checked) {
                    return {
                      ...currentValue,
                      [pointName]: {
                        evaluations: {},
                        ...currentValue[pointName],
                        conditions: [...(currentValue?.[pointName]?.conditions || []), conditionName]
                      }
                    };
                  } else {
                    return {
                      ...currentValue,
                      [pointName]: {
                        evaluations: {},
                        ...currentValue[pointName],
                        conditions: (currentValue[pointName]?.conditions || []).filter((c) => c !== conditionName)
                      }
                    };
                  }
                });
              }}
              onEvaluationsChange={(pointName, evaluations) => {
                setCurrentPointValues((currentValue): ImageDxPointValues => {
                  return {
                    ...currentValue,
                    [pointName]: {
                      evaluations: {
                        ...currentValue?.[pointName]?.evaluations,
                        ...evaluations
                      },
                      conditions: currentValue?.[pointName]?.conditions || []
                    }
                  };
                });
              }}
            />
          ))}
        </ImageViewport>
      ))}
    </ImageSelector>
  );
};
